import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Plus, Upload, FileText, Download, Pencil, Trash2, Star, FolderPlus, Eye, EyeOff } from 'lucide-react';
import { PageHeader, Modal, Field, Spinner, Empty, Toggle, FileInput, ConfirmDialog, PageLoader, fmtDate, fmtSize } from '../../components/ui';
import api, { errMsg, downloadFile } from '../../services/api';

function DocumentModal({ doc, categories, onClose, onSaved }) {
  const [form, setForm] = useState({
    title: doc?.title || '', description: doc?.description || '', categoryId: doc?.categoryId || categories[0]?.id || '',
    published: doc ? doc.published : true, important: doc?.important || false, notify: false,
  });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const save = async () => {
    if (!doc && !file) return toast.error('Please choose a file');
    setBusy(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      if (file) fd.append('file', file);
      const { data } = doc ? await api.put(`/admin/documents/${doc.id}`, fd) : await api.post('/admin/documents', fd);
      toast.success(doc ? 'Document updated' : 'Document uploaded');
      if (data.notified) toast.success(`Emailing ${data.notified} member(s) about it`);
      onSaved();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const willPublishNow = form.published && (!doc || !doc.published);
  return (
    <Modal title={doc ? 'Edit document' : 'Upload document'} onClose={onClose} footer={<>
      <button className="btn-outline" onClick={onClose}>Cancel</button>
      <button className="btn-primary" onClick={save} disabled={busy}>{busy && <Spinner size={16} />}{doc ? 'Save' : 'Upload'}</button>
    </>}>
      <div className="space-y-4">
        <Field label={doc ? 'Replace file' : 'File'} required={!doc} hint="PDF, Word, Excel, PowerPoint or images — up to 25 MB">
          <FileInput file={file} onChange={f => { setFile(f); if (f && !form.title) set('title', f.name.replace(/\.[^.]+$/, '')); }} existing={doc?.originalName} accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.jpg,.jpeg,.png,.txt,.csv" />
        </Field>
        <Field label="Title" required><input className="input" value={form.title} onChange={e => set('title', e.target.value)} /></Field>
        <Field label="Category" required>
          <select className="input" value={form.categoryId} onChange={e => set('categoryId', e.target.value)}>
            {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </Field>
        <Field label="Description"><textarea className="input" rows={2} value={form.description} onChange={e => set('description', e.target.value)} /></Field>
        <div className="space-y-3 rounded-lg bg-gray-50 p-4">
          <Toggle checked={form.published} onChange={v => set('published', v)} label="Published" description="Visible to all verified members" />
          <Toggle checked={form.important} onChange={v => set('important', v)} label="Mark as important" description="Highlighted in the library" />
          <Toggle checked={form.notify} disabled={!form.published} onChange={v => set('notify', v)} label="Email members about this document"
            description={willPublishNow || doc?.published ? 'Members who opted in to document emails will be notified' : 'Publish the document to notify members'} />
        </div>
      </div>
    </Modal>
  );
}

function CategoriesModal({ categories, onClose, onChanged }) {
  const [name, setName] = useState('');
  const [editing, setEditing] = useState(null);
  const add = async e => {
    e.preventDefault();
    try {
      await api.post('/admin/document-categories', { name });
      setName('');
      onChanged();
    } catch (err) { toast.error(errMsg(err)); }
  };
  const rename = async c => {
    try {
      await api.put(`/admin/document-categories/${c.id}`, { name: editing.name, description: editing.description });
      setEditing(null);
      onChanged();
    } catch (err) { toast.error(errMsg(err)); }
  };
  const del = async c => {
    if (!window.confirm(`Delete the category "${c.name}"?`)) return;
    try { await api.delete(`/admin/document-categories/${c.id}`); onChanged(); } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <Modal title="Document categories" onClose={onClose}>
      <ul className="mb-4 divide-y rounded-lg border">
        {categories.map(c => (
          <li key={c.id} className="flex items-center gap-2 px-3 py-2 text-sm">
            {editing?.id === c.id ? (
              <>
                <input className="input py-1" value={editing.name} onChange={e => setEditing(x => ({ ...x, name: e.target.value }))} autoFocus />
                <button className="btn-primary btn-sm" onClick={() => rename(c)}>Save</button>
              </>
            ) : (
              <>
                <span className="flex-1"><span className="font-medium">{c.name}</span>{c.description && <span className="block text-xs text-gray-500">{c.description}</span>}</span>
                <span className="text-xs text-gray-400">{c._count.documents} doc(s)</span>
                <button className="rounded p-1 text-gray-400 hover:text-navy-700" onClick={() => setEditing({ id: c.id, name: c.name, description: c.description })}><Pencil size={14} /></button>
                <button className="rounded p-1 text-gray-400 hover:text-red-600" onClick={() => del(c)}><Trash2 size={14} /></button>
              </>
            )}
          </li>
        ))}
      </ul>
      <form onSubmit={add} className="flex gap-2">
        <input className="input" placeholder="New category name" value={name} onChange={e => setName(e.target.value)} required />
        <button className="btn-primary"><Plus size={16} />Add</button>
      </form>
    </Modal>
  );
}

export default function AdminDocuments() {
  const [docs, setDocs] = useState(null);
  const [categories, setCategories] = useState([]);
  const [filter, setFilter] = useState('');
  const [editing, setEditing] = useState(null); // null | 'new' | doc
  const [showCats, setShowCats] = useState(false);
  const [deleting, setDeleting] = useState(null);

  const load = () => {
    api.get('/admin/documents').then(r => setDocs(r.data));
    api.get('/admin/document-categories').then(r => setCategories(r.data));
  };
  useEffect(load, []);

  const togglePublish = async d => {
    try {
      await api.put(`/admin/documents/${d.id}`, { published: !d.published });
      toast.success(d.published ? 'Unpublished' : 'Published');
      load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  if (!docs) return <PageLoader />;
  const shown = filter ? docs.filter(d => String(d.categoryId) === filter) : docs;

  return (
    <div>
      <PageHeader title="Group documents" subtitle="Members-only library shown in every verified member's profile"
        actions={<>
          <button onClick={() => setShowCats(true)} className="btn-outline"><FolderPlus size={16} />Categories</button>
          <button onClick={() => setEditing('new')} disabled={!categories.length} className="btn-gold"><Upload size={16} />Upload document</button>
        </>} />

      <div className="mb-4 flex flex-wrap gap-2">
        <button onClick={() => setFilter('')} className={`rounded-full px-3 py-1.5 text-sm ${!filter ? 'bg-navy-900 text-white' : 'bg-white text-gray-600 ring-1 ring-gray-200'}`}>All <span className="opacity-60">{docs.length}</span></button>
        {categories.map(c => (
          <button key={c.id} onClick={() => setFilter(String(c.id))} className={`rounded-full px-3 py-1.5 text-sm ${filter === String(c.id) ? 'bg-navy-900 text-white' : 'bg-white text-gray-600 ring-1 ring-gray-200'}`}>
            {c.name} <span className="opacity-60">{c._count.documents}</span>
          </button>
        ))}
      </div>

      <div className="card overflow-hidden">
        {!shown.length ? <Empty icon={FileText} title="No documents yet">Upload financial reports, minutes and admission details for members.</Empty> : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50"><tr><th className="th">Document</th><th className="th">Category</th><th className="th">Size</th><th className="th">Status</th><th className="th">Added</th><th className="th"></th></tr></thead>
              <tbody className="divide-y">
                {shown.map(d => (
                  <tr key={d.id} className="hover:bg-gray-50">
                    <td className="td">
                      <p className="flex items-center gap-1.5 font-medium text-navy-900">{d.title}{d.important && <Star size={13} className="fill-gold-400 text-gold-500" />}</p>
                      <p className="max-w-xs truncate text-xs text-gray-400">{d.originalName}</p>
                    </td>
                    <td className="td text-gray-600">{d.category.name}</td>
                    <td className="td text-gray-500">{fmtSize(d.size)}</td>
                    <td className="td">{d.published ? <span className="badge bg-green-100 text-green-800">Published</span> : <span className="badge bg-gray-100 text-gray-600">Hidden</span>}</td>
                    <td className="td whitespace-nowrap text-gray-500">{fmtDate(d.createdAt)}{d.uploadedBy && <span className="block text-xs text-gray-400">by {d.uploadedBy.name}</span>}</td>
                    <td className="td">
                      <div className="flex justify-end gap-1">
                        <button title={d.published ? 'Unpublish' : 'Publish'} onClick={() => togglePublish(d)} className="rounded p-1.5 text-gray-500 hover:bg-gray-100">{d.published ? <EyeOff size={16} /> : <Eye size={16} />}</button>
                        <button title="Download" onClick={() => downloadFile(`/admin/documents/${d.id}/download`, d.originalName)} className="rounded p-1.5 text-gray-500 hover:bg-gray-100"><Download size={16} /></button>
                        <button title="Edit" onClick={() => setEditing(d)} className="rounded p-1.5 text-gray-500 hover:bg-gray-100"><Pencil size={16} /></button>
                        <button title="Delete" onClick={() => setDeleting(d)} className="rounded p-1.5 text-gray-500 hover:bg-red-50 hover:text-red-600"><Trash2 size={16} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {editing && <DocumentModal doc={editing === 'new' ? null : editing} categories={categories} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      {showCats && <CategoriesModal categories={categories} onClose={() => setShowCats(false)} onChanged={load} />}
      {deleting && (
        <ConfirmDialog title="Delete document?" danger confirmLabel="Delete" message={`"${deleting.title}" will be removed from the library for all members.`}
          onClose={() => setDeleting(null)} onConfirm={async () => { await api.delete(`/admin/documents/${deleting.id}`); toast.success('Deleted'); load(); }} />
      )}
    </div>
  );
}
